import angular = require("angular");
import { CrewPermissionsService } from "./crew-permissions.service";

export interface IAngularDependencyId {
    $angularInjectionId: string;
}

export interface IControllerDescription {
    ControllerClass: any;
    AngularInjectionId: string;
    AngularDependencies: IAngularDependencyId[];
    StateString: string;
    Url: string;
    Template: string;
    HeaderText: string;
}

export interface IDirective {
}

export interface IStandardPageDirectiveClass {
    ComponentDescription: IStandardPageDescription;
}

export interface IStandardComponentDirectiveClass {
    ComponentDescription: IStandardComponentDescription;
}

export interface IViewModelClass {
}

export interface IDependencyDescription {
    AngularDependencies: IAngularDependencyId[];
}

export interface IComponentWithDescription {
    ComponentDescription: IDependencyDescription;
}

export interface IStandardPageDescription {
    DirectiveId: string;
    AngularDependencies: IAngularDependencyId[];
    StateString: string;
    Url: string;
    Params?: any;
    Template: string;
    HeaderText: string;
    IsAbstract?: boolean;
    Filters?: IFilterClassTypeBase[];
}

export interface IStandardComponentDescription {
    DirectiveId: string;
    AngularDependencies: IAngularDependencyId[];
    Template: string;
    Bindings?: {};
    Link?: (scope: ng.IScope, element, attributes) => void;
    Filters?: IFilterClassTypeBase[];
}

export interface ITwoWayConverterComponentDescriptionBase {
    DirectiveId: string;
    AngularDependencies: IAngularDependencyId[];
    ConvertFromSource: (any) => any;
    ConvertToSource: (any) => any;
}

export interface ITwoWayConverterComponentDescription<T_Source, T_Target> extends ITwoWayConverterComponentDescriptionBase {
    DirectiveId: string;
    AngularDependencies: IAngularDependencyId[];
    ConvertFromSource: (T_Source) => T_Target;
    ConvertToSource: (T_Target) => T_Source;
}

export interface IConverterClassTypeBase {
    ComponentDescription: ITwoWayConverterComponentDescriptionBase;
}

export interface IConverterClassType<T_Source, T_Target> extends IConverterClassTypeBase {
    ComponentDescription: ITwoWayConverterComponentDescription<T_Source, T_Target>;
}

interface IControllerClassType {
    ComponentDescription: IControllerDescription;
}

export interface IFilterConstructor<T_FilterFuncSignature> {
    (...args: any[]): T_FilterFuncSignature;
}

interface IFilterDescriptionBase {
    FilterConstructor: any;
    AngularInjectionId: string;
    AngularDependencies: IAngularDependencyId[];
}

export interface IFilterDescription<T_FilterFuncSignature> extends IFilterDescriptionBase {
    FilterConstructor: IFilterConstructor<T_FilterFuncSignature>;
    AngularInjectionId: string;
    AngularDependencies: IAngularDependencyId[];
}

export interface IFilterClassTypeBase {
    ComponentDescription: IFilterDescriptionBase;
}

export interface IFilterClassType<T_FilterFuncSignature> extends IFilterClassTypeBase {
    ComponentDescription: IFilterDescription<T_FilterFuncSignature>;
}

export interface IFilterModuleDescription {
    AngularInjectionId: string;
    Filters: IFilterClassTypeBase[];
}

export interface IConverterModuleDescription {
    AngularInjectionId: string;
    Converters: IConverterClassTypeBase[];
}

interface IClassTypeInjection {
    $inject: string[];
    $angularInjectionId: string;
}

// Must be called in code after class definition
function configureController(controllerClass: IControllerClassType) {
    var controllerDescription = controllerClass.ComponentDescription;
    var controllerInjection = <IClassTypeInjection><any>controllerClass;
    var $inject: string[] = [];
    controllerInjection.$inject = $inject;
    controllerInjection.$angularInjectionId = controllerDescription.AngularInjectionId;

    if (controllerDescription) {
        for (var i = 0; i < controllerDescription.AngularDependencies.length; i++) {
            var injectionItem = controllerDescription.AngularDependencies[i];
            $inject.push(injectionItem.$angularInjectionId);
        }
    }
};

export interface IViewModel {
    Controller: IStandardController;
    scopeInitialized();
}

export interface IStandardController {
}

export function initializeController(module: ng.IModule, controllerClass: IControllerClassType) {
    configureController(controllerClass);
    module.controller(controllerClass.ComponentDescription.AngularInjectionId, <any>controllerClass);
}

function getIntectionIds(dependencies: (string | IAngularDependencyId)[]): string[] {
    var inject: string[] = [];

    for (var i = 0; i < dependencies.length; i++) {
        var injectionItem = dependencies[i];
        if (typeof injectionItem === "string") {
            inject.push(injectionItem);
        }
        else {
            inject.push(injectionItem.$angularInjectionId);
        }
    }

    return inject;
}

function injectAngularDependencies(injectionTarget: any, injectionId: string, dependencies: IAngularDependencyId[]) {
    var $inject: string[] = getIntectionIds(dependencies);
    injectionTarget.$inject = $inject;
    injectionTarget.$angularInjectionId = injectionId;
}

export function initializePageDirective(mod: ng.IModule, directiveClass: IStandardPageDirectiveClass) {
    var conf = directiveClass.ComponentDescription;

    injectAngularDependencies(directiveClass, conf.DirectiveId, directiveClass.ComponentDescription.AngularDependencies);
    
    mod.directive(conf.DirectiveId, directiveWithTemplate);
    
    function directiveWithTemplate(): any {
        return {
            restrict: "A",
            scope: {},
            bindToController: true,
            template: conf.Template,
            controllerAs: "vm",
            controller: directiveClass,
        }
    }

    if (conf.Filters) {
        for (var i = 0; i < conf.Filters.length; i++) {
            registerFilter(mod, conf.Filters[i]);
        }
    }
}

function initializeComponentDirective(mod: ng.IModule, directiveClass: IStandardComponentDirectiveClass) {
    var conf = directiveClass.ComponentDescription;
    injectAngularDependencies(directiveClass, conf.DirectiveId, conf.AngularDependencies);

    var directive: any = function () {
        var directive = {
            restrict: 'E',
            scope: conf.Bindings,
            bindToController: true,
            controller: directiveClass,
            controllerAs: "vm",
            template: conf.Template
        }
        if (conf.Link) {
            (<any>directive).link = conf.Link;
        }

        return directive;
    };

    mod.directive(conf.DirectiveId, directive);

    if (conf.Filters) {
        for (var i = 0; i < conf.Filters.length; i++) {
            registerFilter(mod, conf.Filters[i]);
        }
    }
}

export function registerFilter(mod: ng.IModule, filter: IFilterClassTypeBase) {
    injectAngularDependencies(filter, filter.ComponentDescription.AngularInjectionId, filter.ComponentDescription.AngularDependencies);
    mod.filter(filter.ComponentDescription.AngularInjectionId, filter.ComponentDescription.FilterConstructor);
}

export function registerConverter(mod: ng.IModule, converter: IConverterClassTypeBase) {
    injectAngularDependencies(converter, converter.ComponentDescription.DirectiveId, converter.ComponentDescription.AngularDependencies);

    mod.directive(converter.ComponentDescription.DirectiveId, function () {
        var directive = {
            restrict: "A",
            require: "ngModel",
            link: function (_scope, _element, _attr, ngModel) {
                ngModel.$parsers.push(converter.ComponentDescription.ConvertToSource);
                ngModel.$formatters.push(converter.ComponentDescription.ConvertFromSource);
            }
        }

        return directive;
    });
}

interface ISimpleFilterDescription<T_FilterFuncSignature> {
    AngularInjectionId: string;
    Filter: T_FilterFuncSignature;
}

export function simpleFilter<T_FilterFuncSignature>(description: ISimpleFilterDescription<T_FilterFuncSignature>): IFilterClassType<T_FilterFuncSignature> {
    return {
        ComponentDescription: {
            AngularDependencies: [],
            AngularInjectionId: description.AngularInjectionId,
            FilterConstructor: () => description.Filter
        }
    }
}

export function applyOnFirstNotNull($scope: ng.IScope, watchExpression: string, func: () => void) {
    var listener = $scope.$watch(watchExpression, (newValue, _oldValue) => {
        if (newValue) {
            func();
            listener();
        }
    });

}

export function wireUpPageDirectiveRoute(_mod: ng.IModule, stateProvider: ng.ui.IStateProvider, directiveClass: IStandardPageDirectiveClass) {
    var conf = directiveClass.ComponentDescription;

    var mainView = {
        template: "<div " + conf.DirectiveId + "></div>"
    };

    stateProvider.state("app." + conf.StateString, {
        url: conf.Url,
        params: conf.Params,
        abstract: conf.IsAbstract,
        views: {
            "main-view@app": mainView,
            "heading-view@app": {
                template: conf.HeaderText
            }
        }
    });
}

export function registerPageModule(registeredApplicationModuleIds: string[], directiveClass: IStandardPageDirectiveClass) {
    registeredApplicationModuleIds.push(directiveClass.ComponentDescription.DirectiveId);
    var pageModule = angular.module(directiveClass.ComponentDescription.DirectiveId, ["ui.router", 'ui.router.upgrade']);

    initializePageDirective(pageModule, directiveClass);

    var configure = ($stateProvider: ng.ui.IStateProvider) => {
        wireUpPageDirectiveRoute(pageModule, $stateProvider, directiveClass);
    };
    configure.$inject = ["$stateProvider"];

    pageModule.config(configure);
}

export function registerComponentModule(registeredApplicationModuleIds: string[], directiveClass: IStandardComponentDirectiveClass) {
    registeredApplicationModuleIds.push(directiveClass.ComponentDescription.DirectiveId);
    var componentModule = angular.module(directiveClass.ComponentDescription.DirectiveId, []);
    initializeComponentDirective(componentModule, directiveClass);
}

export function registerFilterModule(registeredApplicationModuleIds: string[], filterModuleDescription: IFilterModuleDescription) {
    registeredApplicationModuleIds.push(filterModuleDescription.AngularInjectionId);
    var filterModule = angular.module(filterModuleDescription.AngularInjectionId, []);
    for (var i = 0; i < filterModuleDescription.Filters.length; i++) {
        registerFilter(filterModule, filterModuleDescription.Filters[i]);
    }
}

export function registerConverterModule(registeredApplicationModuleIds: string[], filterModuleDescription: IConverterModuleDescription) {
    registeredApplicationModuleIds.push(filterModuleDescription.AngularInjectionId);
    var filterModule = angular.module(filterModuleDescription.AngularInjectionId, []);
    for (var i = 0; i < filterModuleDescription.Converters.length; i++) {
        registerConverter(filterModule, filterModuleDescription.Converters[i]);
    }
}

export class DependencyLocator {
    private _map: {};

    constructor(dependencies: IAngularDependencyId[], injected: any[]) {
        this._map = {};
        for (var i = 0; i < injected.length; i++) {
            this._map[dependencies[i].$angularInjectionId] = injected[i];
        }
    }

    public static CreateController(controllerClass: IControllerClassType, injected: any[]) {
        return new DependencyLocator(controllerClass.ComponentDescription.AngularDependencies, injected);
    }

    public static Create(directiveClass: IComponentWithDescription, injected: any[]) {
        return new DependencyLocator(directiveClass.ComponentDescription.AngularDependencies, injected);
    }

    public getDependency(dependency: IAngularDependencyId): any {
        return this.getDependencyById(dependency.$angularInjectionId);
    }

    public getDependencyById(dependencyId: string): any {
        var dependency = this._map[dependencyId];
        if (!dependency) {
            throw "Dependency with Id, '" + dependencyId + "', could not be resolved.";
        }

        return dependency;
    }

    public getScopeDependency(): ng.IScope {
        return this.getDependency(ScopeDependency);
    }

    public getRootScopeDependency(): ng.IScope {
        return this.getDependency(RootScopeDependency);
    }

    public getFilterDependency(): ng.IFilterService {
        return this.getDependency(FilterDependency);
    }

    public getLogDependency(): ng.ILogService {
        return this.getDependency(LogDependency);
    }

    public getStateParamsDependency(): ng.ui.IStateParamsService {
        return this.getDependency(StateParamsDependency);
    }

    public getStateDependency(): ng.ui.IStateService {
        return this.getDependency(StateDependency);
    }

    public getMdDialogDependency(): ng.material.IDialogService {
        return this.getDependency(MdDialogDependency);
    }

    public getHTTPDependency(): ng.IHttpService {
        return this.getDependency(HTTPDependency);
    }

    public getCrewPermissionsDependency(): CrewPermissionsService {
        return this.getDependency(CrewPermissions);
    }
}

export var ScopeDependency: IAngularDependencyId = {
    $angularInjectionId: "$scope"
}

export var RootScopeDependency: IAngularDependencyId = {
    $angularInjectionId: "$rootScope"
}

export var FilterDependency: IAngularDependencyId = {
    $angularInjectionId: "$filter"
}

export var LogDependency: IAngularDependencyId = {
    $angularInjectionId: "$log"
}

export var StateParamsDependency: IAngularDependencyId = {
    $angularInjectionId: "$stateParams"
}

export var StateDependency: IAngularDependencyId = {
    $angularInjectionId: "$state"
}

export var LocationDependency: IAngularDependencyId = {
    $angularInjectionId: "$location"
}

export var MdDialogDependency: IAngularDependencyId = {
    $angularInjectionId: "$mdDialog"
}

export var HTTPDependency: IAngularDependencyId = {
    $angularInjectionId: "$http"
}

export var AuthServerProvider: IAngularDependencyId = {
    $angularInjectionId: "authServerProvider"
}

export var CrewPermissions: IAngularDependencyId = {
    $angularInjectionId: "crewPermissionsService"
}

export function safeScopeApply($scope: ng.IScope) {
    if ($scope && !$scope.$$phase && !$scope.$root.$$phase) $scope.$apply();
}

/**
 * Defines an interface for installing modules.
 */
export interface IInstaller {

    /**
     * Installs all the components.
     */
    install(registeredApplicationModuleIds: string[]): void;

    /**
     * Gets the Pages/Views defined in this installer.
     */
    pages: IStandardPageDirectiveClass[];

    /**
     * Gets the Controls/Directives defined in this installer.
     */
    controls: IStandardComponentDirectiveClass[];

    /**
     * Gets the nested Installers associated with this installer.
     */
    installers: IInstaller[];
}

/**
 * Provides a base class implementation of an IInstaller.
 */
export class BaseInstaller implements IInstaller {

    private _pages: IStandardPageDirectiveClass[];
    private _controls: IStandardComponentDirectiveClass[];
    private _installers: IInstaller[];

    constructor(pages: IStandardPageDirectiveClass[] = null, controls: IStandardComponentDirectiveClass[] = null, installers: IInstaller[] = null) {
        this._pages = pages;
        this._controls = controls;
        this._installers = installers;
    }

    public install(registeredApplicationModuleIds: string[]): void {

        
        // Register the pages
        for (var i = 0; i < this.pages.length; i++) {
            registerPageModule(registeredApplicationModuleIds, this.pages[i]);
        }

        // Register the controls
        for (var i = 0; i < this.controls.length; i++) {
            registerComponentModule(registeredApplicationModuleIds, this.controls[i]);
        }

        // Install nested installers
        for (var i = 0; i < this.installers.length; i++) {
            this.installers[i].install(registeredApplicationModuleIds);
        }
    }

    public get pages(): IStandardPageDirectiveClass[] {
        return this._pages || [];
    }

    public get controls(): IStandardComponentDirectiveClass[] {
        return this._controls || [];
    }

    public get installers(): IInstaller[] {
        return this._installers || [];
    }
}


// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/@angularclass/hmr-loader!./node_modules/tslint-loader!./client/app/core/uicore.ts